import * as React from 'react';
import { useState, ChangeEvent, FormEvent } from 'react';
import '../Chat.css';
import { IoSendOutline } from 'react-icons/io5';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { MdClear } from 'react-icons/md';
import StyledButton, { LoadingSpinner } from '../StyledHtmlElements/Button';
import Box from '../StyledHtmlElements/Box';
import { cn } from '@/lib/utils';

interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {}
const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(({ className = '', ...props }, ref) => (
  <textarea className={`textarea ${className}`} ref={ref} {...props} />
));

Textarea.displayName = 'Textarea';

type Props = {
  onSendMessage: (message: string) => void;
  isLoading: boolean;
  clearConversation: () => void;
  disabled?: boolean;
};

const ChatInputBox = ({ onSendMessage, isLoading, clearConversation, disabled }: Props) => {
  const [input, setInput] = useState<string>('');

  const handleInputChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
  };

  const handleSubmit = (event?: FormEvent<HTMLFormElement>) => {
    if (event) {
      event.preventDefault(); // Prevent default form submission
    }

    if (isLoading || !input.trim()) {
      return; // Prevent sending empty messages or when loading
    }

    onSendMessage(input.trim());
    setInput(''); // Clear input after sending
  };

  return (
    <form onSubmit={handleSubmit} className="flex-1 px-8 ">
      <Box className="relative h-full items-center gap-2 justify-center md:pr-8 flex flex-col">
        <Textarea
          className="bg-transparent h-full rounded-lg text-neutralDark placeholder:text-neutralDark/70 "
          placeholder="Geben Sie hier Ihre Nachricht ein..."
          value={input}
          onChange={handleInputChange}
          onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && !isLoading && handleSubmit()}
          disabled={isLoading}
        />

        <TooltipProvider>
          <div className="w-full flex items-center">
            <Tooltip>
              <TooltipTrigger>
                <StyledButton
                  variant={'outline'}
                  className="rounded-full p-3"
                  type="reset"
                  onClick={!isLoading && !disabled ? clearConversation : undefined}>
                  <MdClear className={isLoading || disabled ? 'opacity-50' : ''} />
                </StyledButton>
              </TooltipTrigger>
              <TooltipContent className="bg-secondary/40 text-neutralDark  text-sm">
                <p>Neuer Chat</p>
              </TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger className="ml-auto">
                <StyledButton
                  variant={'outline'}
                  type="submit"
                  className={cn('rounded-full p-3', disabled ? 'opacity-50 cursor-not-allowed' : '')}
                  disabled={disabled}>
                  {isLoading ? <LoadingSpinner /> : <IoSendOutline />}
                </StyledButton>
              </TooltipTrigger>
              <TooltipContent className="bg-secondary/40 text-neutralDark  text-sm">
                <p>Nachricht senden</p>
              </TooltipContent>
            </Tooltip>
          </div>
        </TooltipProvider>
      </Box>
    </form>
  );
};

export default ChatInputBox;
